import { Cross1Icon, PersonIcon } from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { UsersIcon } from "lucide-react";
import { use, useCallback, useEffect, useRef, useState } from "react";
import { useFocusWithin } from "react-aria";
import { flushSync } from "react-dom";
import {
  Editor,
  RecordProps,
  T,
  TLAsset,
  TLBinding,
  TLShape,
  useEditor,
} from "tldraw";

import { andSiblingCount } from "@/components/canvas-editor/and-siblings";
import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  BlockSeparator,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  Dimensions,
  InclusionOperator,
  SocialListProps,
  SocialListShape,
  inclusionOperatorOptions,
  inclusionOperators,
  metadataType,
  socialListType,
} from "@/components/canvas-editor/shapes/shapes";
import { UserAvatar } from "@/components/canvas-editor/UserAvatar";
import {
  CustomNodeEditorContext,
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { ListWrapper } from "@/components/hooks/ListContainer";
import {
  SimpleNodeButton,
  SimpleNodeInput,
} from "@/components/SimpleEditor/SimpleNode";
import { Avatar } from "@/components/ui/avatar";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { getHydratedUserDidQueryOptions } from "@/lib/api";

const ROW_HEIGHT = 48;

export function isSocialListShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is SocialListShape {
  return shape.type === socialListType;
}

export class SocialListTool extends ToolStateNode {
  static override id = socialListType;

  static override uiToolDef = (editor: Editor) => ({
    id: socialListType,
    icon: UsersIcon,
    label: SocialListShapeUtil.docs.title,
    kbd: "q",
    defaultColor: SocialListShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(socialListType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={SocialList}
        util={SocialListShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: socialListType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class SocialListShapeUtil extends BindableUtil<SocialListShape> {
  static override migrations = createMigrations(socialListType);
  static override type = socialListType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Users",
    description: "Include or exclude a set of users from the feed.",
  };

  override getType() {
    return socialListType;
  }

  static override props: RecordProps<SocialListShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    dids: T.arrayOf(T.string),
    operator: T.literalEnum(...inclusionOperators),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 350,
      minW: 350,
      h: 184,
      dids: [""],
      operator: inclusionOperators[0],
      metadata: undefined,
    };
  }

  override onBeforeCreate(next: SocialListShape): void | SocialListShape {
    const { dids } = next.props;

    if (dids.length > 1) {
      next.props.h += (dids.length - 1) * ROW_HEIGHT;
    }
  }

  override component(shape: SocialListShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <SocialList
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

export function UserRow({
  did,
  onChange,
  onDelete,
  variant = "canvas",
}: {
  did: string;
  onChange: (did: string) => void;
  onDelete?: () => void;
  variant?: "canvas" | "simple";
}) {
  const isCustomNodeEditor = use(CustomNodeEditorContext);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: user } = useQuery(getHydratedUserDidQueryOptions(did));

  useEffect(() => {
    if (!inputRef.current || !(inputRef.current instanceof HTMLInputElement)) {
      return;
    }

    if (user) {
      inputRef.current?.setCustomValidity("");
    } else if (!isCustomNodeEditor) {
      inputRef.current.setCustomValidity(
        "Please enter a valid user DID or handle.",
      );
    }
  }, [user, isCustomNodeEditor]);

  const InputComponent = variant === "canvas" ? CanvasInput : SimpleNodeInput;
  const ButtonComponent =
    variant === "canvas" ? CanvasButton : SimpleNodeButton;

  return (
    <div className="relative group flex gap-2 items-center">
      <UserAvatar did={did} />
      <InputComponent
        aria-label="User DID or handle"
        placeholder="User DID or handle"
        ref={inputRef}
        value={did}
        className="flex-1"
        onValueChange={onChange}
      />

      {onDelete && (
        <ButtonComponent
          size="smIcon"
          variant="ghost"
          className="pointer-events-auto absolute right-1 top-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
          onPress={onDelete}
        >
          <Cross1Icon />
        </ButtonComponent>
      )}
    </div>
  );
}

function UserRowReadOnly({ did }: { did: string }) {
  const { data: user } = useQuery(getHydratedUserDidQueryOptions(did));
  const avatar = (
    <Avatar
      src={user?.avatar}
      alt={user?.avatar || ""}
      fallback={<PersonIcon />}
    />
  );

  return (
    <div className="relative group flex gap-2 items-center">
      {user ? (
        <SimpleTooltip
          side="left"
          message={
            <div>
              <div>{user.display_name || user.handle}</div>
              <div className="text-xs text-slate-dim">@{user.handle}</div>
            </div>
          }
        >
          {avatar}
        </SimpleTooltip>
      ) : (
        avatar
      )}
    </div>
  );
}

/**
 * Say what an Include list does to everything else in the feed.
 *
 * A Users node defaults to Include (`inclusionOperators[0]` is `"in"`), and
 * dropped into the feed's `and` that reads "the author must be one of these
 * handles" -- so a node added meaning "show me this person too" silently removes
 * every other account instead. Feed 34972 went empty exactly that way on
 * 2026-09-21: one handle, ANDed with a keyword filter, and nothing anywhere said
 * the feed had just been narrowed to one person.
 *
 * Only shown when the node is bound into the feed AND shares an `and` with
 * something else, which is the only arrangement where the restriction is
 * surprising. A feed that IS a Users node restricts nothing beyond itself, and
 * an `or` branch widens rather than narrows -- see `and-siblings.ts`.
 *
 * It states an effect rather than reporting an error, because for 425 of the
 * live feeds in this shape it is the intended one. Nothing is blocked and no
 * default is changed: Include is what most authors of these nodes want (1,149
 * live feeds use Include against 626 that use Exclude), so the fix for the
 * minority is to make the effect legible, not to move the trapdoor somewhere
 * else.
 */
function RestrictsFeedNotice({
  shape,
  isBound,
}: {
  shape: SocialListShape;
  isBound: boolean;
}) {
  const editor = useEditor();

  // A node sitting loose on the canvas, or in a group not wired to the feed,
  // is not filtering anything yet -- saying "everything else in the feed is
  // filtered out" about it would simply be false.
  if (!isBound) return null;
  if (shape.props.operator !== "in") return null;
  if (andSiblingCount(editor, shape) === 0) return null;

  const named = shape.props.dids.filter((did) => did.trim() !== "").length;

  // A node still being filled in has nothing to name yet, and "these 0
  // accounts" would be worse than silence. The list becomes restrictive the
  // moment it has an entry, which is when this starts speaking.
  if (named === 0) return null;

  return (
    <span className="text-xs text-amber-11 dark:text-amberdark-11">
      Only posts by {named === 1 ? "this account" : `these ${named} accounts`}{" "}
      can match — everything else in the feed is filtered out. Switch to Exclude
      to leave the rest of the feed alone.
    </span>
  );
}

function SocialList({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: SocialListShape;
  className?: string;
  updateShape: (
    shape: SocialListShape,
    props: Partial<SocialListProps>,
  ) => void;
}) {
  const isReadOnly = useIsReadonlyEditor() || shape.props.isReadOnly;
  const variant = shape.props.operator === "in" ? "green" : "red";
  const ref = useRef<HTMLDivElement>(null);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: setIsFocusWithin,
  });
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );
  const onAddAnother = useCallback(() => {
    // eslint-disable-next-line @eslint-react/dom/no-flush-sync
    flushSync(() => {
      updateShape(shape, { dids: [...shape.props.dids, ""] });
    });

    const inputs = ref.current?.querySelectorAll<HTMLInputElement>("input");

    if (!inputs) {
      return;
    }

    inputs.values().toArray().at(-1)?.focus();
  }, [shape, updateShape]);

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        ref={ref}
        {...focusWithinProps}
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
        onKeyDownCapture={(e) => {
          if (e.key === "Enter" && e.metaKey) {
            e.preventDefault();
            e.stopPropagation();

            onAddAnother();
          }
        }}
      >
        <NodeBlockHeader props={shape.props} docs={SocialListShapeUtil.docs} />
        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <CanvasSimpleSelect
              aria-label="Inclusion Operator"
              value={shape.props.operator}
              onValueChange={(v) =>
                updateShape(shape, { operator: v as InclusionOperator })
              }
              options={inclusionOperatorOptions}
            />
            {!isReadOnly && (
              <RestrictsFeedNotice shape={shape} isBound={isBound} />
            )}
            <BlockSeparator />

            {isReadOnly ? (
              <div className="flex flex-wrap gap-2">
                {shape.props.dids.map((did) => (
                  <UserRowReadOnly did={did} key={did} />
                ))}
              </div>
            ) : (
              <>
                <ListWrapper>
                  {shape.props.dids.map((did, index) => (
                    <UserRow
                      // eslint-disable-next-line @eslint-react/no-array-index-key
                      key={`${shape.id}-list-member-${index}`}
                      did={did}
                      onChange={(u) => {
                        const dids = [...shape.props.dids];
                        dids[index] = u.replace(/^@/, "").trim();
                        updateShape(shape, { dids });
                      }}
                      onDelete={
                        index === 0
                          ? undefined
                          : () => {
                              const dids = [...shape.props.dids];
                              dids.splice(index, 1);
                              updateShape(shape, { dids });
                            }
                      }
                    />
                  ))}
                </ListWrapper>
                <CanvasButton
                  size="sm"
                  variant="ghost"
                  onPress={onAddAnother}
                  className="relative"
                >
                  Add Another
                  {isFocusWithin && (
                    <KbdGroup className="hidden md:flex absolute top-1/2 right-2 -translate-y-1/2">
                      <Kbd>Meta + ⏎</Kbd>
                    </KbdGroup>
                  )}
                </CanvasButton>
              </>
            )}
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
